import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { enableMapSet } from "immer";
import { NOTIFICATION_WS_URL } from "@/constants";

// Enable Immer support for Map and Set (must be called before store creation)
// Race condition with enableMapSet in App.tsx
enableMapSet();

export type SubscriptionType = "project" | "organization"; // Add other types as needed

export interface SubscriptionConfig {
  type: SubscriptionType;
  id: string | undefined;
}

export enum NOTIFICATIONS_EVENT_TYPE {
  ProjectStatusChanged = "ProjectStatusChanged",
  DocumentStatusChanged = "DocumentStatusChanged",
  StoryboardStatusChanged = "StoryboardStatusChanged",
  MediaStatusChanged = "MediaStatusChanged",
  SlideStatusChanged = "SlideStatusChanged",
  ScriptSpecsSaved = "ScriptSpecsSaved",
  BrandkitStatusChanged = "BrandkitStatusChanged",
  DocumentInsightSaved = "DocumentInsightSaved",
  VoiceProfileStatusChanged = "VoiceProfileStatusChanged",
  AddOnStatusChanged = "AddOnStatusChanged",
  // Publication status (MED-6885) — fires on every publication state change,
  // including automatic ones (no polling needed after a publish action).
  PublicationStatusChanged = "PublicationStatusChanged",
  // Live-analytics events (per-project, same project_id routing)
  ProjectViewersUpdate = "ProjectViewersUpdate",
  NewMaxConcurrentViewers = "NewMaxConcurrentViewers",
  ViewMilestoneReached = "ViewMilestoneReached",
  ProjectTrending = "ProjectTrending",
  ChatbotMessageAnswered = "ChatbotMessageAnswered",
  QuizSubmitted = "QuizSubmitted",
  // Add other event types as needed
}

export enum NotificationStatus {
  ExtractingContent = "ExtractingContent",
  StoryboardGenerating = "StoryboardGenerating",
  SlidesGenerating = "SlidesGenerating",
  PostProcessing = "PostProcessing",
  Ready = "Ready",
  SlidesGenerated = "SlidesGenerated",
  StoryboardApproved = "StoryboardApproved",
  ContentExtracted = "ContentExtracted",
  Published = "Published",
  Generated = "Generated",
}

export interface WSEvent {
  event: NOTIFICATIONS_EVENT_TYPE;
  organization_id: string;
  status: string;
  project_id?: string;
  document_id?: string;
  storyboard_id?: string;
  media_id?: string;
  section_id?: string;
  asset_id?: string;
  storyboard_settings_id?: string;
  // AddOnStatusChanged-only: which add-on this event is about (AddOnType value).
  add_on_type?: string;
  // PublicationStatusChanged-only fields.
  instance_id?: string;
  has_live_publication?: boolean;
  // VoiceProfileStatusChanged-only fields. project_id is also populated for
  // routing — set to the voice_profile_id by the BE as a workaround.
  voice_profile_id?: string;
  operation?: "Cloning" | "PreviewGeneration";
  // Live-analytics payload fields
  viewer_count?: number; // ProjectViewersUpdate
  max_concurrent?: number; // NewMaxConcurrentViewers
  total_views?: number; // ViewMilestoneReached
  milestone?: number; // ViewMilestoneReached
  score?: number | null; // QuizSubmitted (percentage)
  // Add other properties as needed, match ${config.type}_id format
}

// Alias for backward compatibility
export type ProjectNotification = WSEvent;

type Handler = (event: WSEvent) => void;

interface Subscription {
  config: SubscriptionConfig;
  handlers: Set<Handler>;
}

interface WebSocketState {
  ws: WebSocket | null;
  isConnected: boolean;
  isConnecting: boolean;
  connectionAttempts: number;
  subscriptions: Map<string, Subscription>;
  token: string | null;
  needsNewToken: boolean;
}

interface WebSocketActions {
  setToken: (token: string | null) => void;
  connect: () => void;
  disconnect: () => void;
  subscribe: (config: SubscriptionConfig, handler: Handler) => () => void;
  unsubscribe: (config: SubscriptionConfig, handler: Handler) => void;
}

type WebSocketStore = WebSocketState & WebSocketActions;

const dispatchEventToSubscription = (
  { config, handlers }: Subscription,
  event: WSEvent
) => {
  const eventIdKey = `${config.type}_id` as keyof WSEvent;
  const eventId = event[eventIdKey];
  const matches = eventId === config.id;

  if (matches) {
    handlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        // Isolate handler errors - one failing handler shouldn't break others
        console.error("[WS] Handler error:", error);
      }
    });
  }
};

const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_INTERVAL = 3000;
const DISCONNECT_DELAY = 3000; // debounce delay for disconnecting on empty subscriptions

export const getSubscriptionKey = (config: SubscriptionConfig): string =>
  `${config.type}:${config.id}`;

const initialState: WebSocketState = {
  ws: null,
  isConnected: false,
  isConnecting: false,
  connectionAttempts: 0,
  subscriptions: new Map(),
  token: null,
  needsNewToken: false,
};

let disconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

// Reconnect when the tab regains visibility if the connection was lost
// while backgrounded (e.g. server idle timeout, throttled reconnect exhaustion).
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    const { isConnected, isConnecting, subscriptions, token, needsNewToken } =
      useWebSocketStore.getState();
    if (
      !isConnected &&
      !isConnecting &&
      subscriptions.size > 0 &&
      token &&
      !needsNewToken
    ) {
      useWebSocketStore.setState(
        { connectionAttempts: 0 },
        false,
        "visibilityReconnect"
      );
      useWebSocketStore.getState().connect();
    }
  });
}

const cancelPendingDisconnect = () => {
  if (disconnectTimeoutId) {
    clearTimeout(disconnectTimeoutId);
    disconnectTimeoutId = null;
  }
};

// Cancel scheduled reconnect to prevent reconnecting after explicit disconnect()
const cancelPendingReconnect = () => {
  if (reconnectTimeoutId) {
    clearTimeout(reconnectTimeoutId);
    reconnectTimeoutId = null;
  }
};

export const useWebSocketStore = create<WebSocketStore>()(
  devtools(
    immer((set, get) => ({
      ...initialState,

      setToken: (token: string | null) => {
        const { token: currentToken, isConnected, subscriptions } = get();

        // Token changed
        if (token !== currentToken) {
          cancelPendingDisconnect();

          // Only disconnect on logout (token cleared), not on token refresh.
          // A healthy WS connection doesn't need re-authentication — the token
          // was used at connect time. Store the fresh token for future reconnects.
          if (!token && isConnected) {
            get().disconnect();
          }

          set(
            (state) => {
              state.token = token;
              // Reset connection state when receiving a fresh token
              if (token) {
                state.needsNewToken = false;
                state.connectionAttempts = 0;
              }
            },
            false,
            "setToken"
          );

          // Auto-connect if we have a token, subscriptions, but no connection
          if (token && subscriptions.size > 0 && !isConnected) {
            get().connect();
          }
        }
      },

      connect: () => {
        const { ws, isConnecting, token, needsNewToken } = get();
        // Cancel any pending disconnect
        cancelPendingDisconnect();
        if (!token || needsNewToken) {
          return;
        }
        if (ws?.readyState === WebSocket.OPEN || isConnecting) {
          return;
        }
        // Close existing connection if any
        if (ws) {
          ws.close();
        }

        set(
          (state) => {
            state.isConnecting = true;
          },
          false,
          "connect"
        );

        const socketUrl = `${NOTIFICATION_WS_URL}?token=${token}`;
        const newWs = new WebSocket(socketUrl);

        newWs.onopen = () => {
          set(
            (state) => {
              state.ws = newWs;
              state.isConnected = true;
              state.isConnecting = false;
              state.connectionAttempts = 0;
            },
            false,
            "open"
          );

          // Subscribe to all pending subscriptions
          const { subscriptions } = get();
          subscriptions.forEach(({ config }) => {
            const message = {
              action: "subscribe",
              [`${config.type}_id`]: config.id,
            };
            newWs.send(JSON.stringify(message));
          });
        };

        newWs.onmessage = ({ data }) => {
          try {
            const event: WSEvent = JSON.parse(data);
            const { subscriptions } = get();

            subscriptions.forEach((subscription) =>
              dispatchEventToSubscription(subscription, event)
            );
          } catch (error) {
            console.error("[WS] Failed to parse message:", error);
          }
        };

        newWs.onclose = (event) => {
          // Detect authentication/token errors
          // 1008: Policy violation (auth failure)
          // 4401: Custom auth error code (if backend uses it)
          // 1002: Protocol error (sometimes used for auth)
          const isAuthError = [1008, 4401, 1002].includes(event.code);

          set(
            (state) => {
              state.isConnected = false;
              state.isConnecting = false;
              state.ws = null;
              // Request new token if connection closed due to auth error
              if (isAuthError) {
                state.needsNewToken = true;
              }
            },
            false,
            "close"
          );

          if (isAuthError) {
            return;
          }

          // Reconnect for abnormal closes (only if we still have subscriptions)
          const {
            subscriptions,
            connectionAttempts,
            token: storedToken,
          } = get();
          if (!event.wasClean && subscriptions.size > 0) {
            if (connectionAttempts < MAX_RECONNECT_ATTEMPTS && storedToken) {
              set(
                (state) => {
                  state.connectionAttempts = connectionAttempts + 1;
                },
                false,
                "reconnect"
              );
              reconnectTimeoutId = setTimeout(() => {
                reconnectTimeoutId = null;
                get().connect();
              }, RECONNECT_INTERVAL);
            } else {
              // Request new token after exhausting reconnection attempts
              set(
                (state) => {
                  state.needsNewToken = true;
                },
                false,
                "needsNewToken"
              );
            }
          }
        };

        newWs.onerror = (error) => {
          console.error("[WS] Error:", error);
        };

        set(
          (state) => {
            state.ws = newWs;
          },
          false,
          "init"
        );
      },

      disconnect: () => {
        cancelPendingDisconnect();
        cancelPendingReconnect();
        const { ws } = get();
        if (ws) {
          ws.close(1000, "Client disconnect");
        }
        // Keep token, just close connection
        set(
          (state) => {
            state.ws = null;
            state.isConnected = false;
            state.isConnecting = false;
            state.connectionAttempts = 0;
          },
          false,
          "disconnect"
        );
      },

      subscribe: (config, handler) => {
        const { ws, isConnected, token } = get();
        const key = getSubscriptionKey(config);

        // Cancel any pending disconnect if we have new subscriptions
        cancelPendingDisconnect();

        const existing = get().subscriptions.get(key);

        if (existing) {
          // Add handler to existing subscription
          set(
            (state) => {
              state.subscriptions.get(key)?.handlers.add(handler);
            },
            false,
            `addHandler [${key}]`
          );
        } else {
          // Create new subscription
          set(
            (state) => {
              state.subscriptions.set(key, {
                config,
                handlers: new Set([handler]),
              });
            },
            false,
            `subscribe [${key}]`
          );

          // Send subscribe message if connected
          if (ws && isConnected) {
            const message = {
              action: "subscribe",
              [`${config.type}_id`]: config.id,
            };
            ws.send(JSON.stringify(message));
          }
        }

        // Auto-connect if we have token but not connected
        if (token && !isConnected && !get().isConnecting) {
          get().connect();
        }

        // Return unsubscribe function for convenience
        return () => get().unsubscribe(config, handler);
      },

      unsubscribe: (config, handler) => {
        const { ws, isConnected } = get();
        const key = getSubscriptionKey(config);

        const existing = get().subscriptions.get(key);

        if (!existing) return;

        // Check if this is the last handler BEFORE mutating
        const isLastHandler =
          existing.handlers.size === 1 && existing.handlers.has(handler);

        // All mutations must happen inside set() with Immer
        set(
          (state) => {
            const sub = state.subscriptions.get(key);
            if (sub) {
              sub.handlers.delete(handler);
              // Remove subscription if no more handlers
              if (sub.handlers.size === 0) {
                state.subscriptions.delete(key);
              }
            }
          },
          false,
          `unsubscribe [${key}]${isLastHandler ? " (last)" : ""}`
        );

        // Send unsubscribe to server if this was the last handler
        if (isLastHandler && ws && isConnected) {
          const message = {
            action: "unsubscribe",
            [`${config.type}_id`]: config.id,
          };
          ws.send(JSON.stringify(message));
        }

        // Schedule disconnect if no more subscriptions (with delay)
        if (get().subscriptions.size === 0 && isConnected) {
          disconnectTimeoutId = setTimeout(() => {
            // Double-check we still have no subscriptions
            if (get().subscriptions.size === 0) {
              get().disconnect();
            }
          }, DISCONNECT_DELAY);
        }
      },
    })),
    { name: "WebSocketStore" }
  )
);
